import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  storeMediaFile,
  retrieveMediaFile,
  deleteMediaFile,
  getStoredTranscript,
  setStoredTranscript,
  deleteStoredTranscript,
  clearAllStoredTranscripts,
} from "../utils/mediaStorage";
import { toast } from "react-hot-toast";
import i18n from "../i18n";

// Prevent noisy duplicate toasts for existing A–B ranges
let lastDuplicateToastAt = 0;
const DUPLICATE_TOAST_ID = "bookmark-duplicate";

const revokeObjectUrl = (url?: string | null) => {
  if (url && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

export interface MediaFile {
  name: string;
  type: string;
  size: number;
  url: string;
  id?: string;
  storageId?: string; // ID for IndexedDB storage
}

export interface YouTubeMedia {
  id: string;
  title?: string;
}

export interface LoopBookmark {
  id: string;
  name: string;
  start: number;
  end: number;
  createdAt: number;
  mediaName?: string;
  mediaType?: string;
  youtubeId?: string;
  playbackRate?: number;
  annotation?: string;
}

// New interface for media-scoped bookmarks
export interface MediaBookmarks {
  [mediaId: string]: LoopBookmark[];
}

export interface MediaHistoryItem {
  id: string;
  type: "file" | "youtube";
  name: string;
  accessedAt: number;
  folderId?: string | null;
  playbackTime?: number;
  fileData?: Omit<MediaFile, "id">;
  youtubeData?: {
    title?: string;
    youtubeId?: string;
  };
  storageId?: string; // ID for IndexedDB storage
}

export interface MediaFolder {
  id: string;
  name: string;
  createdAt: number;
  parentId: string | null;
}

export interface TranscriptSegment {
  id: string;
  text: string;
  startTime: number;
  endTime: number;
  confidence: number;
  isFinal: boolean;
}

// New interface for media-scoped transcripts
export interface MediaTranscripts {
  [mediaId: string]: TranscriptSegment[];
}

export interface PlayerState {
  // Media state
  currentFile: MediaFile | null;
  currentYouTube: YouTubeMedia | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  mediaVolume: number; // New separate media volume
  previousMediaVolume?: number; // Store media volume before muting
  previousVolume?: number; // Store volume before muting
  playbackRate: number;
  muted: boolean;
  isLoadingMedia: boolean; // Add loading state

  // Loop state
  loopStart: number | null;
  loopEnd: number | null;
  isLooping: boolean;
  loopCount: number;
  maxLoops: number;
  autoAdvanceBookmarks: boolean;
  bpm: number | null;
  quantizeLoop: boolean;
  loopDelay: number; // Delay in seconds between loops

  // UI state
  theme: "light" | "dark";
  waveformZoom: number;
  showWaveform: boolean;
  videoSize: "sm" | "md" | "lg" | "xl";
  mediaBookmarks: MediaBookmarks; // Changed from bookmarks array to media-scoped object
  selectedBookmarkId: string | null;
  // Seek configuration
  seekStepSeconds: number; // default seek step for arrows/buttons
  seekSmallStepSeconds: number; // shift+arrow small step

  // Transcript state
  mediaTranscripts: MediaTranscripts; // Changed from transcriptSegments array to media-scoped object
  isTranscriptLoading: boolean;
  showTranscript: boolean;
  isTranscribing: boolean;
  transcriptLanguage: string;

  // History and sharing
  recentYouTubeVideos: YouTubeMedia[];
  mediaHistory: MediaHistoryItem[];
  historyLimit: number;
  // Library organization & sorting
  mediaFolders: Record<string, MediaFolder>;
  historySortBy: "date" | "name" | "type";
  historySortOrder: "asc" | "desc";
  historyFolderFilter: "all" | "unfiled" | string;
}

export interface PlayerActions {
  // Media actions
  setCurrentFile: (file: MediaFile | null) => void;
  setCurrentYouTube: (youtube: YouTubeMedia | null) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setVolume: (volume: number) => void;
  setMediaVolume: (volume: number) => void;
  setPreviousMediaVolume: (volume: number) => void;
  setPreviousVolume: (volume: number) => void;
  setPlaybackRate: (rate: number) => void;
  setMuted: (muted: boolean) => void;
  togglePlay: () => void;
  toggleMute: () => void;
  seekForward: (seconds: number) => void;
  seekBackward: (seconds: number) => void;
  setIsLoadingMedia: (loading: boolean) => void; // Add loading action

  // Loop actions
  setLoopPoints: (start: number | null, end: number | null) => void;
  setIsLooping: (isLooping: boolean) => void;
  setLoopCount: (count: number) => void;
  setMaxLoops: (max: number) => void;
  toggleLooping: () => void;
  moveLoopWindow: (deltaTime: number) => void;
  extendLoopStart: (deltaTime: number) => void;
  extendLoopEnd: (deltaTime: number) => void;
  scaleLoop: (factor: number) => void;
  setBpm: (bpm: number | null) => void;
  setQuantizeLoop: (quantize: boolean) => void;
  quantizeCurrentLoop: () => void;
  setLoopDelay: (delay: number) => void;

  // UI actions
  setTheme: (theme: "light" | "dark") => void;
  setWaveformZoom: (zoom: number) => void;
  setShowWaveform: (show: boolean) => void;
  setVideoSize: (size: "sm" | "md" | "lg" | "xl") => void;
  setAutoAdvanceBookmarks: (enable: boolean) => void;
  // Seek settings
  setSeekStepSeconds: (seconds: number) => void;
  setSeekSmallStepSeconds: (seconds: number) => void;

  // Transcript actions
  startTranscribing: () => void;
  stopTranscribing: () => void;
  toggleTranscribing: () => void;
  addTranscriptSegment: (segment: Omit<TranscriptSegment, "id">) => void;
  updateTranscriptSegment: (
    id: string,
    changes: Partial<TranscriptSegment>
  ) => void;
  clearTranscript: () => void;
  setShowTranscript: (show: boolean) => void;
  toggleShowTranscript: () => void;
  setTranscriptLanguage: (language: string) => void;
  exportTranscript: (format: "txt" | "srt" | "vtt") => string;
  importTranscript: (file: File) => Promise<void>;
  createBookmarkFromTranscript: (segmentId: string) => void;
  loadTranscriptForMedia: (mediaId: string) => Promise<void>;

  // Bookmark actions
  addBookmark: (bookmark: Omit<LoopBookmark, "id" | "createdAt">) => boolean;
  updateBookmark: (id: string, changes: Partial<LoopBookmark>) => void;
  deleteBookmark: (id: string) => void;
  loadBookmark: (id: string) => void;
  setSelectedBookmarkId: (id: string | null) => void;
  importBookmarks: (bookmarks: LoopBookmark[]) => void;

  // Helper functions for media-scoped bookmarks
  getCurrentMediaId: () => string | null;
  getCurrentMediaBookmarks: () => LoopBookmark[];

  // Helper functions for media-scoped transcripts
  getCurrentMediaTranscripts: () => TranscriptSegment[];

  // History actions
  addRecentYouTubeVideo: (video: YouTubeMedia) => void;
  clearRecentYouTubeVideos: () => void;
  addToMediaHistory: (
    item: Omit<MediaHistoryItem, "id" | "accessedAt">
  ) => void;
  updateHistoryPlaybackTime: (id: string, time: number) => void;
  loadFromHistory: (historyItemId: string) => void;
  removeFromHistory: (historyItemId: string) => Promise<void>;
  clearMediaHistory: () => Promise<void>;
  setHistoryLimit: (limit: number) => void;

  // Folder & item management
  createMediaFolder: (name: string, parentId?: string | null) => string;
  renameMediaFolder: (folderId: string, newName: string) => void;
  deleteMediaFolder: (folderId: string) => void;
  moveHistoryItemToFolder: (historyItemId: string, folderId: string | null) => void;
  renameHistoryItem: (historyItemId: string, newName: string) => void;
  setHistorySort: (
    by: "date" | "name" | "type",
    order: "asc" | "desc"
  ) => void;
  setHistoryFolderFilter: (filter: "all" | "unfiled" | string) => void;
}

const initialState: PlayerState = {
  currentFile: null,
  currentYouTube: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  mediaVolume: 1,
  playbackRate: 1,
  muted: false,
  loopStart: null,
  loopEnd: null,
  isLooping: false,
  loopCount: 0,
  maxLoops: 0,
  autoAdvanceBookmarks: false,
  bpm: null,
  quantizeLoop: false,
  loopDelay: 0,
  theme: "dark",
  waveformZoom: 1,
  showWaveform: true,
  videoSize: "md",
  mediaBookmarks: {},
  selectedBookmarkId: null,
  mediaTranscripts: {},
  isTranscriptLoading: false,
  showTranscript: false,
  isTranscribing: false,
  transcriptLanguage: "en-US",
  recentYouTubeVideos: [],
  mediaHistory: [],
  historyLimit: 30,
  isLoadingMedia: false,
  // Defaults for seek steps
  seekStepSeconds: 5,
  seekSmallStepSeconds: 1,
  // Library organization & sorting defaults
  mediaFolders: {},
  historySortBy: "date",
  historySortOrder: "desc",
  historyFolderFilter: "unfiled",
};

export const usePlayerStore = create<PlayerState & PlayerActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      async loadTranscriptForMedia(mediaId: string) {
        set({ isTranscriptLoading: true });

        const segments = await getStoredTranscript(mediaId);

        set((state) => {
          const nextTranscripts = {
            ...state.mediaTranscripts,
            [mediaId]: segments,
          };

          return {
            mediaTranscripts: nextTranscripts,
            isTranscriptLoading:
              state.getCurrentMediaId() === mediaId ? false : state.isTranscriptLoading,
          };
        });
      },

      // Media actions
      setCurrentFile: async (file) => {
        if (file) {
          try {
            const previousUrl = get().currentFile?.url;
            let storageId = file.storageId;

            if (file instanceof File && !storageId) {
              try {
                // Pass current storageId so cleanup never evicts the playing file
                const currentStorageId = get().currentFile?.storageId;
                const excludeIds = currentStorageId ? [currentStorageId] : [];
                storageId = await storeMediaFile(
                  file,
                  undefined,
                  undefined,
                  excludeIds
                );
              } catch (error) {
                console.error("Failed to store file in IndexedDB:", error);
              }
            }

            // Check if this file already exists in history by name and size or storageId
            const { mediaHistory } = get();
            let existingHistoryItem = null;

            if (storageId) {
              // First try to find by storageId (most reliable)
              existingHistoryItem = mediaHistory.find(
                (item) => item.type === "file" && item.storageId === storageId
              );
            }

            if (!existingHistoryItem) {
              // Then try by filename and size
              existingHistoryItem = mediaHistory.find(
                (item) =>
                  item.type === "file" &&
                  item.fileData?.name === file.name &&
                  item.fileData?.size === file.size
              );
            }

            // Use existing ID if found in history, otherwise generate a new one
            const fileId = existingHistoryItem
              ? existingHistoryItem.id.replace("history-", "file-")
              : `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            const fileWithId = {
              ...file,
              id: file.id || fileId,
              storageId,
            };

            // Add to history with storage ID
            get().addToMediaHistory({
              type: "file",
              name: fileWithId.name,
              fileData: {
                name: fileWithId.name,
                type: fileWithId.type,
                size: fileWithId.size,
                url: fileWithId.url,
              },
              storageId,
            });

            // Resuming playback time if available in history
            const startQuery = new URLSearchParams(window.location.search).get("t");
            let initialTime = 0;
            if (startQuery) {
              initialTime = parseFloat(startQuery) || 0;
            } else if (existingHistoryItem?.playbackTime) {
              initialTime = existingHistoryItem.playbackTime;
            }

            set({
              currentFile: fileWithId,
              currentYouTube: null,
              currentTime: initialTime,
              isPlaying: false,
              isTranscriptLoading: true,
              // Reset loop points and selected bookmark when switching media
              loopStart: null,
              loopEnd: null,
              isLooping: false,
              selectedBookmarkId: null,
            });

            const mediaId =
              fileWithId.storageId ||
              fileWithId.id ||
              `file-${fileWithId.name}-${fileWithId.size}`;
            await get().loadTranscriptForMedia(mediaId);

            if (previousUrl && previousUrl !== fileWithId.url) {
              revokeObjectUrl(previousUrl);
            }
          } catch (error) {
            console.error("Error setting current file:", error);
            toast.error(i18n.t("history.failedToLoadMedia"));

            revokeObjectUrl(get().currentFile?.url);
            set({
              currentFile: null,
              currentTime: 0,
              isPlaying: false,
              isTranscriptLoading: false,
              loopStart: null,
              loopEnd: null,
              isLooping: false,
              selectedBookmarkId: null,
            });
          }
        } else {
          revokeObjectUrl(get().currentFile?.url);
          set({
            currentFile: null,
            currentTime: 0,
            isPlaying: false,
            isTranscriptLoading: false,
            loopStart: null,
            loopEnd: null,
            isLooping: false,
            selectedBookmarkId: null,
          });
        }
      },
      setCurrentYouTube: async (youtube) => {
        revokeObjectUrl(get().currentFile?.url);
        if (youtube) {
          get().addRecentYouTubeVideo(youtube);

          // Also add to general history
          get().addToMediaHistory({
            type: "youtube",
            name: youtube.title || `YouTube Video: ${youtube.id}`,
            youtubeData: {
              title: youtube.title,
              youtubeId: youtube.id,
            },
          });
        }

        // Find if we have a saved time for this video
        const { mediaHistory } = get();
        const existingHistoryItem = mediaHistory.find(
          (item) =>
            item.type === "youtube" &&
            item.youtubeData?.youtubeId === youtube?.id
        );

        const startQuery = new URLSearchParams(window.location.search).get("t");
        let initialTime = 0;
        if (startQuery) {
          initialTime = parseFloat(startQuery) || 0;
        } else if (existingHistoryItem?.playbackTime) {
          initialTime = existingHistoryItem.playbackTime;
        }

        set({
          currentYouTube: youtube,
          currentFile: null,
          currentTime: initialTime,
          isPlaying: false,
          isTranscriptLoading: !!youtube,
          // Reset loop points and selected bookmark when switching media
          loopStart: null,
          loopEnd: null,
          isLooping: false,
          selectedBookmarkId: null,
        });

        if (youtube?.id) {
          await get().loadTranscriptForMedia(`youtube-${youtube.id}`);
        } else {
          set({ isTranscriptLoading: false });
        }
      },
      setIsPlaying: (isPlaying) => set({ isPlaying }),
      setCurrentTime: (currentTime) => set({ currentTime }),
      setDuration: (duration) => set({ duration }),
      setVolume: (volume) => set({ volume }),
      setMediaVolume: (mediaVolume) => set({ mediaVolume }),
      setPreviousMediaVolume: (previousMediaVolume) => set({ previousMediaVolume }),
      setPreviousVolume: (previousVolume) => set({ previousVolume }),
      setPlaybackRate: (playbackRate) => set({ playbackRate }),
      setMuted: (muted) => set({ muted }),
      togglePlay: () => set((state) => ({ isPlaying: !state.isPlaying })),
      toggleMute: () => {
        const { muted, volume, previousVolume } = get();
        if (muted) {
          // Unmuting
          if (volume === 0) {
            if (previousVolume !== undefined && previousVolume > 0) {
              set({ volume: previousVolume, muted: false });
            } else {
              set({ volume: 1, muted: false });
            }
          } else {
            set({ muted: false });
          }
        } else {
          // Muting
          set({ previousVolume: volume, volume: 0, muted: true });
        }

      },

      // Seek forward/backward
      seekForward: (seconds) => {
        const { currentTime, duration } = get();
        const newTime = Math.min(currentTime + seconds, duration);
        set({ currentTime: newTime });
      },

      seekBackward: (seconds) => {
        const { currentTime } = get();
        const newTime = Math.max(currentTime - seconds, 0);
        set({ currentTime: newTime });
      },

      // Loop actions
      setLoopPoints: (loopStart, loopEnd) => set({ loopStart, loopEnd }),
      setIsLooping: (isLooping) => set({ isLooping }),
      setLoopCount: (loopCount) => set({ loopCount }),
      setMaxLoops: (maxLoops) => set({ maxLoops }),
      toggleLooping: () => {
        const { isLooping, currentTime, getCurrentMediaBookmarks } = get();

        if (isLooping) {
          // If already looping, just turn it off
          set({ isLooping: false });
        } else {
          // If turning on, check if we are inside a bookmark
          const bookmarks = getCurrentMediaBookmarks();
          const coveringBookmarks = bookmarks.filter(
            (b) => b.start <= currentTime && b.end >= currentTime
          );

          if (coveringBookmarks.length > 0) {
            // Find the shortest bookmark
            const shortest = coveringBookmarks.reduce((prev, curr) => {
              const prevDur = prev.end - prev.start;
              const currDur = curr.end - curr.start;
              return currDur < prevDur ? curr : prev;
            });

            set({
              isLooping: true,
              loopStart: shortest.start,
              loopEnd: shortest.end,
              selectedBookmarkId: shortest.id,
              loopCount: 0,
            });
            toast.success(i18n.t("bookmarks.loopingBookmark", { name: shortest.name }));
          } else {
            // Default behavior: just toggle on (restores previous loop or enables if set)
            // If no loop points set, user might expect something, but let's stick to standard toggle
            set({ isLooping: true, loopCount: 0 });
          }
        }
      },
      moveLoopWindow: (deltaTime) => {
        const { loopStart, loopEnd, duration } = get();
        if (loopStart === null || loopEnd === null) return;

        let newStart = loopStart + deltaTime;
        let newEnd = loopEnd + deltaTime;

        // Ensure we stay within valid bounds
        if (newStart < 0) {
          const shift = -newStart;
          newStart = 0;
          newEnd = Math.min(newEnd + shift, duration);
        }

        if (newEnd > duration) {
          const shift = newEnd - duration;
          newEnd = duration;
          newStart = Math.max(newStart - shift, 0);
        }

        set({ loopStart: newStart, loopEnd: newEnd });
      },
      extendLoopStart: (deltaTime) => {
        const { loopStart, loopEnd } = get();
        if (loopStart === null || loopEnd === null) return;

        const newStart = Math.max(0, loopStart + deltaTime);
        if (newStart < loopEnd) {
          set({ loopStart: newStart });
        }
      },
      extendLoopEnd: (deltaTime) => {
        const { loopStart, loopEnd, duration } = get();
        if (loopStart === null || loopEnd === null) return;

        const newEnd = Math.min(duration, loopEnd + deltaTime);
        if (newEnd > loopStart) {
          set({ loopEnd: newEnd });
        }
      },
      scaleLoop: (factor) => {
        const { loopStart, loopEnd, duration } = get();
        if (loopStart === null || loopEnd === null) return;

        const center = (loopStart + loopEnd) / 2;
        const halfLength = (loopEnd - loopStart) / 2;
        const newHalfLength = halfLength * factor;

        const newStart = Math.max(0, center - newHalfLength);
        const newEnd = Math.min(duration, center + newHalfLength);

        set({ loopStart: newStart, loopEnd: newEnd });
      },
      setBpm: (bpm) => set({ bpm }),
      setQuantizeLoop: (quantizeLoop) => set({ quantizeLoop }),
      quantizeCurrentLoop: () => {
        const { loopStart, loopEnd, bpm } = get();
        if (loopStart === null || loopEnd === null || !bpm) return;

        // Calculate beat duration in seconds
        const beatDuration = 60 / bpm;

        // Calculate how many beats the current loop spans
        const currentDuration = loopEnd - loopStart;
        const numBeats = Math.round(currentDuration / beatDuration);

        // Ensure at least 1 beat
        const quantizedNumBeats = Math.max(1, numBeats);
        const quantizedDuration = quantizedNumBeats * beatDuration;

        // Calculate the new end time while keeping the start fixed
        const newEnd = loopStart + quantizedDuration;
        set({ loopEnd: newEnd });
      },
      setLoopDelay: (loopDelay) => set({ loopDelay }),

      // UI actions
      setTheme: (theme) => set({ theme }),
      setWaveformZoom: (waveformZoom) => set({ waveformZoom }),
      setShowWaveform: (showWaveform) => set({ showWaveform }),
      setVideoSize: (videoSize) => set({ videoSize }),
      setAutoAdvanceBookmarks: (autoAdvanceBookmarks) => set({ autoAdvanceBookmarks }),
      // Seek settings
      setSeekStepSeconds: (seconds: number) =>
        set({ seekStepSeconds: Math.max(0.1, Math.min(120, seconds)) }),
      setSeekSmallStepSeconds: (seconds: number) =>
        set({ seekSmallStepSeconds: Math.max(0.05, Math.min(10, seconds)) }),

      // Bookmark actions
      addBookmark: (bookmark) => {
        const { getCurrentMediaId } = get();
        const mediaId = getCurrentMediaId();
        if (!mediaId) return false;

        // Prevent duplicates: same start/end within tolerance
        const TOL = 0.05; // 50ms tolerance
        const existing = get().mediaBookmarks[mediaId] || [];
        const isDup = existing.some(
          (b) => Math.abs(b.start - bookmark.start) < TOL && Math.abs(b.end - bookmark.end) < TOL
        );
        if (isDup) {
          const now = Date.now();
          // Show at most once every 1.5s and reuse the same toast id
          if (now - lastDuplicateToastAt > 1500) {
            lastDuplicateToastAt = now;
            toast.error(i18n.t("bookmarks.duplicateRange"), {
              id: DUPLICATE_TOAST_ID,
              duration: 1500,
            });
          }
          return false;
        }

        set((state) => ({
          mediaBookmarks: {
            ...state.mediaBookmarks,
            [mediaId]: [
              ...(state.mediaBookmarks[mediaId] || []),
              {
                ...bookmark,
                id: Date.now().toString(),
                createdAt: Date.now(),
              },
            ],
          },
        }));
        return true;
      },
      updateBookmark: (id, changes) => {
        const { getCurrentMediaId } = get();
        const mediaId = getCurrentMediaId();
        if (!mediaId) return;

        set((state) => ({
          mediaBookmarks: {
            ...state.mediaBookmarks,
            [mediaId]: (state.mediaBookmarks[mediaId] || []).map((bookmark) =>
              bookmark.id === id ? { ...bookmark, ...changes } : bookmark
            ),
          },
        }));
      },
      deleteBookmark: (id) => {
        const { getCurrentMediaId } = get();
        const mediaId = getCurrentMediaId();
        if (!mediaId) return;

        set((state) => ({
          mediaBookmarks: {
            ...state.mediaBookmarks,
            [mediaId]: (state.mediaBookmarks[mediaId] || []).filter(
              (bookmark) => bookmark.id !== id
            ),
          },
          selectedBookmarkId:
            state.selectedBookmarkId === id ? null : state.selectedBookmarkId,
        }));
      },
      loadBookmark: (id) => {
        const { getCurrentMediaBookmarks } = get();
        const bookmarks = getCurrentMediaBookmarks();
        const bookmark = bookmarks.find((b) => b.id === id);

        if (bookmark) {
          set({
            loopStart: bookmark.start,
            loopEnd: bookmark.end,
            isLooping: true,
            selectedBookmarkId: id,
            loopCount: 0,
            ...(bookmark.playbackRate !== undefined
              ? { playbackRate: bookmark.playbackRate }
              : {}),
          });
        }
      },
      setSelectedBookmarkId: (selectedBookmarkId) =>
        set({ selectedBookmarkId }),
      importBookmarks: (bookmarks) => {
        const { getCurrentMediaId } = get();
        const mediaId = getCurrentMediaId();
        if (!mediaId) return;

        // Deduplicate on import by time range (within tolerance)
        const TOL = 0.05;
        const current = get().mediaBookmarks[mediaId] || [];
        const filtered = bookmarks.filter(
          (bm) =>
            !current.some(
              (b) => Math.abs(b.start - bm.start) < TOL && Math.abs(b.end - bm.end) < TOL
            )
        );

        set((state) => ({
          mediaBookmarks: {
            ...state.mediaBookmarks,
            [mediaId]: [...(state.mediaBookmarks[mediaId] || []), ...filtered],
          },
        }));
      },

      // History actions
      addRecentYouTubeVideo: (video) =>
        set((state) => {
          // Check if this video is already in recent videos
          const exists = state.recentYouTubeVideos.some(
            (v) => v.id === video.id
          );
          if (exists) {
            // Move it to the top of the list
            return {
              recentYouTubeVideos: [
                video,
                ...state.recentYouTubeVideos.filter((v) => v.id !== video.id),
              ].slice(0, 10), // Keep only 10 most recent
            };
          } else {
            // Add it to the top
            return {
              recentYouTubeVideos: [video, ...state.recentYouTubeVideos].slice(
                0,
                10
              ), // Keep only 10 most recent
            };
          }
        }),
      clearRecentYouTubeVideos: () => set({ recentYouTubeVideos: [] }),

      // Extended history management
      addToMediaHistory: (item) =>
        set((state) => {
          const id = `history-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`;
          const timestamp = Date.now();

          // Check if this item already exists in history
          let existingItemIndex = -1;

          if (item.type === "file" && item.fileData) {
            // First check by storageId if available (most reliable)
            if (item.storageId) {
              existingItemIndex = state.mediaHistory.findIndex(
                (h) => h.type === "file" && h.storageId === item.storageId
              );
            }

            // If not found by storageId, try by filename and size
            if (existingItemIndex === -1 && item.fileData.name) {
              existingItemIndex = state.mediaHistory.findIndex(
                (h) =>
                  h.type === "file" &&
                  h.fileData?.name === item.fileData?.name &&
                  h.fileData?.size === item.fileData?.size
              );
            }
          } else if (item.type === "youtube" && item.youtubeData) {
            existingItemIndex = state.mediaHistory.findIndex(
              (h) =>
                h.type === "youtube" &&
                h.youtubeData?.youtubeId === item.youtubeData?.youtubeId
            );
          }

          // If item exists, update its timestamp and move to top of history
          if (existingItemIndex >= 0) {
            const updatedHistory = [...state.mediaHistory];
            const existingItem = updatedHistory.splice(existingItemIndex, 1)[0];

            // Update the storageId if the new item has one but the existing one doesn't
            const updatedItem = {
              ...existingItem,
              accessedAt: timestamp,
              // Preserve existing name (keep user renames)
              name: existingItem.name || (item as any).name,
              // Update storageId if the new item has one
              ...(item.storageId && !existingItem.storageId
                ? { storageId: item.storageId }
                : {}),
              // Update fileData if needed
              ...(item.fileData
                ? { fileData: { ...existingItem.fileData, ...item.fileData } }
                : {}),
              // Preserve folder assignment
              folderId: existingItem.folderId ?? null,
            };

            return {
              mediaHistory: [updatedItem, ...updatedHistory].slice(
                0,
                state.historyLimit
              ),
            };
          }

          // Otherwise add as new item
          return {
            mediaHistory: [
              { ...item, id, accessedAt: timestamp, folderId: null },
              ...state.mediaHistory,
            ].slice(0, state.historyLimit),
          };
        }),

      updateHistoryPlaybackTime: (id, time) =>
        set((state) => ({
          mediaHistory: state.mediaHistory.map((item) =>
            item.id === id ? { ...item, playbackTime: time } : item
          ),
        })),

      loadFromHistory: async (historyItemId) => {
        // Set loading state at the beginning
        set({ isLoadingMedia: true });

        const { mediaHistory } = get();
        const historyItem = mediaHistory.find(
          (item) => item.id === historyItemId
        );

        if (!historyItem) {
          set({ isLoadingMedia: false });
          return;
        }

        try {
          if (historyItem.type === "file" && historyItem.fileData) {
            // Check if we have this file stored in IndexedDB
            if (historyItem.storageId) {
              try {
                // Try to retrieve the file from IndexedDB
                const file = await retrieveMediaFile(historyItem.storageId);

                if (file) {
                  // Create a URL for the file
                  const url = URL.createObjectURL(file);
                  console.log(
                    "Retrieved file from IndexedDB:",
                    file,
                    "Created URL:",
                    url
                  );

                  const fileData: MediaFile = {
                    name: historyItem.fileData.name,
                    type: historyItem.fileData.type,
                    size: historyItem.fileData.size,
                    url: url,
                    id: `file-${Date.now()}-${Math.random()
                      .toString(36)
                      .substr(2, 9)}`,
                    storageId: historyItem.storageId,
                  };

                  get().setCurrentFile(fileData);
                  // Restore playback time if available
                  if (historyItem.playbackTime) {
                    set({ currentTime: historyItem.playbackTime });
                  }
                  set({ isLoadingMedia: false });
                  return;
                }
              } catch (error) {
                console.error("Failed to load file from storage:", error);
              }

              // storageId was present but file not found/failed — don't try the stale blob URL
              toast.error(i18n.t("player.couldNotRetrieveMedia"));
              set({ isLoadingMedia: false });
              return;
            }

            // No storageId — try the URL only if it's not a blob (blob URLs don't survive page refresh)
            const savedUrl = historyItem.fileData.url;
            if (!savedUrl || savedUrl.startsWith("blob:")) {
              toast.error(i18n.t("player.couldNotRetrieveMedia"));
              set({ isLoadingMedia: false });
              return;
            }

            const fileData: MediaFile = {
              ...historyItem.fileData,
              id: `file-${Date.now()}-${Math.random()
                .toString(36)
                .substr(2, 9)}`,
            };

            get().setCurrentFile(fileData);
            // Restore playback time if available
            if (historyItem.playbackTime) {
              set({ currentTime: historyItem.playbackTime });
            }
          } else if (
            historyItem.type === "youtube" &&
            historyItem.youtubeData
          ) {
            // Get the YouTube ID from the history item
            const youtubeId = historyItem.youtubeData.youtubeId;
            if (!youtubeId) {
              set({ isLoadingMedia: false });
              return;
            }

            const youtubeData: YouTubeMedia = {
              id: youtubeId,
              title: historyItem.youtubeData.title,
            };
            get().setCurrentYouTube(youtubeData);
            // Restore playback time if available
            if (historyItem.playbackTime) {
              set({ currentTime: historyItem.playbackTime });
            }
          }

          // Update the access timestamp but don't create a new entry
          // Just move this item to the top of the history list
          set((state) => {
            const updatedHistory = [...state.mediaHistory];
            const existingItemIndex = updatedHistory.findIndex(
              (item) => item.id === historyItemId
            );

            if (existingItemIndex >= 0) {
              const existingItem = updatedHistory.splice(
                existingItemIndex,
                1
              )[0];
              return {
                mediaHistory: [
                  { ...existingItem, accessedAt: Date.now() },
                  ...updatedHistory,
                ].slice(0, state.historyLimit),
              };
            }

            return state; // No changes if item not found
          });
        } finally {
          // Always clear loading state when done
          set({ isLoadingMedia: false });
        }
      },

      removeFromHistory: async (historyItemId) => {
        const { mediaHistory } = get();
        const historyItem = mediaHistory.find(
          (item) => item.id === historyItemId
        );

        if (!historyItem) return;

        const derivedMediaId =
          historyItem.type === "file"
            ? historyItem.storageId ||
              (historyItem.fileData
                ? `file-${historyItem.fileData.name}-${historyItem.fileData.size}`
                : null)
            : historyItem.youtubeData?.youtubeId
              ? `youtube-${historyItem.youtubeData.youtubeId}`
              : null;

        const { currentFile, currentYouTube } = get();
        const isDeletingCurrentMedia =
          (historyItem.type === "file" &&
            !!currentFile &&
            ((historyItem.storageId &&
              currentFile.storageId === historyItem.storageId) ||
              (!!historyItem.fileData &&
                currentFile.name === historyItem.fileData.name &&
                currentFile.size === historyItem.fileData.size))) ||
          (historyItem.type === "youtube" &&
            !!currentYouTube &&
            currentYouTube.id === historyItem.youtubeData?.youtubeId);

        // Remove references immediately so the library updates even if storage cleanup is slow.
        set((state) => {
          const nextBookmarks = { ...state.mediaBookmarks };
          const nextTranscripts = { ...state.mediaTranscripts };

          if (derivedMediaId) {
            delete nextBookmarks[derivedMediaId];
            delete nextTranscripts[derivedMediaId];
          }

          return {
            mediaHistory: state.mediaHistory.filter(
              (item) => item.id !== historyItemId
            ),
            mediaBookmarks: nextBookmarks,
            mediaTranscripts: nextTranscripts,
            ...(isDeletingCurrentMedia
              ? {
                  currentFile: null,
                  currentYouTube: null,
                  isPlaying: false,
                  currentTime: 0,
                  duration: 0,
                  loopStart: null,
                  loopEnd: null,
                  isLooping: false,
                  selectedBookmarkId: null,
                }
              : {}),
          };
        });

        if (derivedMediaId) {
          await deleteStoredTranscript(derivedMediaId);

          try {
            const { useShadowingStore } = await import("./shadowingStore");
            await useShadowingStore.getState().deleteAllSegments(derivedMediaId);
          } catch (error) {
            console.error("Failed to delete shadowing segments:", error);
          }
        }

        // If this item has a storage ID, delete the file from IndexedDB
        if (historyItem.storageId) {
          try {
            await deleteMediaFile(historyItem.storageId);
            console.log(
              "Deleted media file from storage:",
              historyItem.storageId
            );
          } catch (error) {
            console.error("Failed to delete media file from storage:", error);
          }
        }

      },

      clearMediaHistory: async () => {
        const mediaIds = get().mediaHistory
          .map((item) =>
            item.type === "file"
              ? item.storageId ||
                (item.fileData
                  ? `file-${item.fileData.name}-${item.fileData.size}`
                  : null)
              : item.youtubeData?.youtubeId
                ? `youtube-${item.youtubeData.youtubeId}`
                : null
          )
          .filter((mediaId): mediaId is string => Boolean(mediaId));

        set({
          mediaHistory: [],
          mediaBookmarks: {},
          mediaTranscripts: {},
          currentFile: null,
          currentYouTube: null,
          isPlaying: false,
          currentTime: 0,
          duration: 0,
          loopStart: null,
          loopEnd: null,
          isLooping: false,
          selectedBookmarkId: null,
          isTranscriptLoading: false,
        });

        try {
          await clearAllStoredTranscripts();

          const { useShadowingStore } = await import("./shadowingStore");
          await Promise.all(
            mediaIds.map((mediaId) =>
              useShadowingStore.getState().deleteAllSegments(mediaId)
            )
          );
        } catch (error) {
          console.error("Failed to clear shadowing sessions:", error);
        }

        try {
          // Clear all media files from IndexedDB
          const { clearAllMediaFiles } = await import("../utils/mediaStorage");
          await clearAllMediaFiles();
          console.log("All media files cleared from storage");
        } catch (error) {
          console.error("Failed to clear media storage:", error);
        }
      },

      setHistoryLimit(limit) {
        set({ historyLimit: limit });
      },

      // Folder & item management
      createMediaFolder: (name, parentId = null) => {
        const id = `folder-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 8)}`;
        set((state) => ({
          mediaFolders: {
            ...state.mediaFolders,
            [id]: {
              id,
              name,
              createdAt: Date.now(),
              parentId: parentId ?? null,
            },
          },
        }));
        return id;
      },
      renameMediaFolder: (folderId, newName) =>
        set((state) => ({
          mediaFolders: {
            ...state.mediaFolders,
            [folderId]: {
              ...state.mediaFolders[folderId],
              name: newName,
            },
          },
        })),
      deleteMediaFolder: (folderId) =>
        set((state) => {
          const removalSet = new Set<string>();
          const stack = [folderId];

          while (stack.length) {
            const current = stack.pop()!;
            removalSet.add(current);
            Object.values(state.mediaFolders).forEach((folder) => {
              if (folder.parentId === current) {
                stack.push(folder.id);
              }
            });
          }

          const mediaFolders = Object.fromEntries(
            Object.entries(state.mediaFolders).filter(
              ([id]) => !removalSet.has(id)
            )
          );

          const mediaHistory = state.mediaHistory.map((item) =>
            item.folderId && removalSet.has(item.folderId)
              ? { ...item, folderId: null }
              : item
          );

          const shouldResetFilter =
            state.historyFolderFilter !== "all" &&
            state.historyFolderFilter !== "unfiled" &&
            typeof state.historyFolderFilter === "string" &&
            removalSet.has(state.historyFolderFilter);

          const historyFolderFilter = shouldResetFilter
            ? "unfiled"
            : state.historyFolderFilter;

          return { mediaFolders, mediaHistory, historyFolderFilter };
        }),
      moveHistoryItemToFolder: (historyItemId, folderId) =>
        set((state) => ({
          mediaHistory: state.mediaHistory.map((item) =>
            item.id === historyItemId ? { ...item, folderId: folderId ?? null } : item
          ),
        })),
      renameHistoryItem: (historyItemId, newName) =>
        set((state) => ({
          mediaHistory: state.mediaHistory.map((item) =>
            item.id === historyItemId
              ? {
                ...item,
                name: newName,
                ...(item.type === "file" && item.fileData
                  ? { fileData: { ...item.fileData, name: newName } }
                  : {}),
                ...(item.type === "youtube" && item.youtubeData
                  ? { youtubeData: { ...item.youtubeData, title: newName } }
                  : {}),
              }
              : item
          ),
        })),
      setHistorySort: (by, order) => set({ historySortBy: by, historySortOrder: order }),
      setHistoryFolderFilter: (filter) => set({ historyFolderFilter: filter }),

      // Transcript actions
      startTranscribing() {
        set({ isTranscribing: true });
      },

      stopTranscribing() {
        set({ isTranscribing: false });
      },

      toggleTranscribing() {
        const { isTranscribing } = get();
        set({ isTranscribing: !isTranscribing });
      },

      addTranscriptSegment(segment) {
        const id = crypto.randomUUID();
        const newSegment = { ...segment, id };
        const { getCurrentMediaId } = get();
        const mediaId = getCurrentMediaId();
        if (!mediaId) return;

        let updatedSegments: TranscriptSegment[] | null = null;

        set((state) => {
          const currentSegments = state.mediaTranscripts[mediaId] || [];

          // Check for duplicates: same start time (approx) and same text
          const isDuplicate = currentSegments.some(
            (s) => Math.abs(s.startTime - newSegment.startTime) < 0.1 && s.text === newSegment.text
          );

          if (isDuplicate) return state;

          updatedSegments = [...currentSegments, newSegment].sort(
            (a, b) => a.startTime - b.startTime
          );

          return {
            mediaTranscripts: {
              ...state.mediaTranscripts,
              [mediaId]: updatedSegments,
            },
          };
        });

        if (updatedSegments) {
          void setStoredTranscript(mediaId, updatedSegments);
        }
      },

      updateTranscriptSegment(id, changes) {
        const { getCurrentMediaId } = get();
        const mediaId = getCurrentMediaId();
        if (!mediaId) return;

        let updatedSegments: TranscriptSegment[] = [];

        set((state) => {
          updatedSegments = (state.mediaTranscripts[mediaId] || []).map((segment) =>
            segment.id === id ? { ...segment, ...changes } : segment
          );

          return {
            mediaTranscripts: {
              ...state.mediaTranscripts,
              [mediaId]: updatedSegments,
            },
          };
        });

        void setStoredTranscript(mediaId, updatedSegments);
      },

      clearTranscript() {
        const { getCurrentMediaId } = get();
        const mediaId = getCurrentMediaId();
        if (!mediaId) return;

        set((state) => ({
          mediaTranscripts: {
            ...state.mediaTranscripts,
            [mediaId]: [],
          },
        }));

        void deleteStoredTranscript(mediaId);
      },

      setShowTranscript(show) {
        set({ showTranscript: show });
      },

      toggleShowTranscript() {
        const { showTranscript } = get();
        set({ showTranscript: !showTranscript });
      },

      setTranscriptLanguage(language) {
        set({ transcriptLanguage: language });
      },

      exportTranscript(format) {
        const { mediaTranscripts, getCurrentMediaId } = get();
        const mediaId = getCurrentMediaId();
        const transcriptSegments = mediaId
          ? mediaTranscripts[mediaId] || []
          : [];

        if (transcriptSegments.length === 0) {
          toast.error(i18n.t("transcript.noDataToExport"));
          return "";
        }

        // Helper functions for time formatting
        function formatTime(seconds: number): string {
          const mins = Math.floor(seconds / 60);
          const secs = Math.floor(seconds % 60);
          return `${mins}:${secs.toString().padStart(2, "0")}`;
        }

        function formatSrtTime(seconds: number): string {
          const hrs = Math.floor(seconds / 3600);
          const mins = Math.floor((seconds % 3600) / 60);
          const secs = Math.floor(seconds % 60);
          const ms = Math.floor((seconds - Math.floor(seconds)) * 1000);
          return `${hrs.toString().padStart(2, "0")}:${mins
            .toString()
            .padStart(2, "0")}:${secs.toString().padStart(2, "0")},${ms
              .toString()
              .padStart(3, "0")}`;
        }

        function formatVttTime(seconds: number): string {
          const hrs = Math.floor(seconds / 3600);
          const mins = Math.floor((seconds % 3600) / 60);
          const secs = Math.floor(seconds % 60);
          const ms = Math.floor((seconds - Math.floor(seconds)) * 1000);
          return `${hrs.toString().padStart(2, "0")}:${mins
            .toString()
            .padStart(2, "0")}:${secs.toString().padStart(2, "0")}.${ms
              .toString()
              .padStart(3, "0")}`;
        }

        // We can use media info for context if needed in the future

        if (format === "txt") {
          // Simple text format
          const text = transcriptSegments
            .map(
              (segment) =>
                `[${formatTime(segment.startTime)} - ${formatTime(
                  segment.endTime
                )}] ${segment.text}`
            )
            .join("\n");

          return text;
        } else if (format === "srt") {
          // SubRip format
          const srt = transcriptSegments
            .map((segment, index) => {
              const startTime = formatSrtTime(segment.startTime);
              const endTime = formatSrtTime(segment.endTime);
              return `${index + 1}\n${startTime} --> ${endTime}\n${segment.text
                }\n`;
            })
            .join("\n");

          return srt;
        } else if (format === "vtt") {
          // WebVTT format
          const vtt = ["WEBVTT\n"];

          transcriptSegments.forEach((segment, index) => {
            const startTime = formatVttTime(segment.startTime);
            const endTime = formatVttTime(segment.endTime);
            vtt.push(
              `${index + 1}\n${startTime} --> ${endTime}\n${segment.text}\n`
            );
          });

          return vtt.join("\n");
        }

        return "";
      },

      async importTranscript(file) {
        try {
          const { getCurrentMediaId, clearTranscript, addTranscriptSegment } =
            get();
          const mediaId = getCurrentMediaId();

          if (!mediaId) {
            toast.error(i18n.t("player.noMediaLoadedSimple"));
            return;
          }

          // Read file content
          const content = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsText(file);
          });

          // Clear existing transcript
          clearTranscript();

          // Parse based on file extension
          const fileName = file.name.toLowerCase();
          let segments: Omit<TranscriptSegment, "id">[] = [];

          if (fileName.endsWith(".srt")) {
            segments = parseSrtContent(content);
          } else if (fileName.endsWith(".vtt")) {
            segments = parseVttContent(content);
          } else if (fileName.endsWith(".txt")) {
            segments = parseTxtContent(content);
          } else {
            // Try to auto-detect format
            if (content.includes("WEBVTT")) {
              segments = parseVttContent(content);
            } else if (
              content.includes("-->") &&
              /^\d+$/.test(content.split("\n")[0]?.trim())
            ) {
              segments = parseSrtContent(content);
            } else {
              segments = parseTxtContent(content);
            }
          }

          // Add all segments to the transcript
          segments.forEach((segment) => {
            addTranscriptSegment(segment);
          });
        } catch (error) {
          console.error("Error importing transcript:", error);
          toast.error(i18n.t("transcript.importError"));
        }

        // Helper functions for parsing different formats
        function parseSrtContent(
          content: string
        ): Omit<TranscriptSegment, "id">[] {
          const segments: Omit<TranscriptSegment, "id">[] = [];
          const normalizedContent = normalizeTranscriptContent(content);
          const blocks = normalizedContent
            .split(/\n{2,}/)
            .filter((block) => block.trim());

          blocks.forEach((block) => {
            const lines = block
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean);

            const timeLineIndex = lines.findIndex((line) => line.includes("-->"));
            if (timeLineIndex === -1) {
              return;
            }

            const timeMatch = lines[timeLineIndex].match(
              /(\d{2}):(\d{2}):(\d{2})[,.](\d{3})\s+-->\s+(\d{2}):(\d{2}):(\d{2})[,.](\d{3})/
            );

            if (!timeMatch) {
              return;
            }

            const textLines = lines.slice(timeLineIndex + 1);
            if (textLines.length === 0) {
              return;
            }

            const startTime = parseTimeToSeconds(
              timeMatch[1],
              timeMatch[2],
              timeMatch[3],
              timeMatch[4]
            );
            const endTime = parseTimeToSeconds(
              timeMatch[5],
              timeMatch[6],
              timeMatch[7],
              timeMatch[8]
            );

            segments.push({
              text: textLines.join(" ").trim(),
              startTime,
              endTime,
              confidence: 1.0,
              isFinal: true,
            });
          });

          return segments;
        }

        function parseVttContent(
          content: string
        ): Omit<TranscriptSegment, "id">[] {
          const segments: Omit<TranscriptSegment, "id">[] = [];
          const lines = normalizeTranscriptContent(content).split("\n");
          let i = 0;

          // Skip WEBVTT header
          while (i < lines.length && !lines[i].includes("-->")) {
            i++;
          }

          while (i < lines.length) {
            const line = lines[i].trim();

            if (line.includes("-->")) {
              const timeMatch = line.match(
                /(\d{2}):(\d{2}):(\d{2})\.(\d{3}) --> (\d{2}):(\d{2}):(\d{2})\.(\d{3})/
              );
              if (timeMatch) {
                const startTime = parseTimeToSeconds(
                  timeMatch[1],
                  timeMatch[2],
                  timeMatch[3],
                  timeMatch[4]
                );
                const endTime = parseTimeToSeconds(
                  timeMatch[5],
                  timeMatch[6],
                  timeMatch[7],
                  timeMatch[8]
                );

                i++;
                const textLines = [];
                while (i < lines.length && lines[i].trim() !== "") {
                  textLines.push(lines[i].trim());
                  i++;
                }

                if (textLines.length > 0) {
                  segments.push({
                    text: textLines.join(" ").trim(),
                    startTime,
                    endTime,
                    confidence: 1.0,
                    isFinal: true,
                  });
                }
              }
            }
            i++;
          }

          return segments;
        }

        function parseTxtContent(
          content: string
        ): Omit<TranscriptSegment, "id">[] {
          const segments: Omit<TranscriptSegment, "id">[] = [];
          const lines = normalizeTranscriptContent(content)
            .split("\n")
            .filter((line) => line.trim());

          lines.forEach((line, index) => {
            const trimmedLine = line.trim();
            if (trimmedLine) {
              // Try to extract timestamps if they exist in format [MM:SS - MM:SS] or [HH:MM:SS - HH:MM:SS]
              const timeMatch = trimmedLine.match(
                /\[(\d{1,2}):(\d{2})(?::(\d{2}))?\s*-\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\]\s*(.+)/
              );

              if (timeMatch) {
                const startHours = timeMatch[3] ? parseInt(timeMatch[1]) : 0;
                const startMinutes = timeMatch[3]
                  ? parseInt(timeMatch[2])
                  : parseInt(timeMatch[1]);
                const startSeconds = timeMatch[3]
                  ? parseInt(timeMatch[3])
                  : parseInt(timeMatch[2]);

                const endHours = timeMatch[6] ? parseInt(timeMatch[4]) : 0;
                const endMinutes = timeMatch[6]
                  ? parseInt(timeMatch[5])
                  : parseInt(timeMatch[4]);
                const endSeconds = timeMatch[6]
                  ? parseInt(timeMatch[6])
                  : parseInt(timeMatch[5]);

                const startTime =
                  startHours * 3600 + startMinutes * 60 + startSeconds;
                const endTime = endHours * 3600 + endMinutes * 60 + endSeconds;

                segments.push({
                  text: timeMatch[7].trim(),
                  startTime,
                  endTime,
                  confidence: 1.0,
                  isFinal: true,
                });
              } else {
                // No timestamps, create segments with estimated timing (5 seconds each)
                const startTime = index * 5;
                const endTime = (index + 1) * 5;

                segments.push({
                  text: trimmedLine,
                  startTime,
                  endTime,
                  confidence: 1.0,
                  isFinal: true,
                });
              }
            }
          });

          return segments;
        }

        function parseTimeToSeconds(
          hours: string,
          minutes: string,
          seconds: string,
          milliseconds: string
        ): number {
          return (
            parseInt(hours) * 3600 +
            parseInt(minutes) * 60 +
            parseInt(seconds) +
            parseInt(milliseconds) / 1000
          );
        }

        function normalizeTranscriptContent(content: string): string {
          return content.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n").trim();
        }
      },

      createBookmarkFromTranscript(segmentId) {
        const {
          mediaTranscripts,
          addBookmark,
          currentFile,
          currentYouTube,
          playbackRate,
          getCurrentMediaId,
        } = get();
        const mediaId = getCurrentMediaId();
        if (!mediaId) return;

        const segment = (mediaTranscripts[mediaId] || []).find(
          (s) => s.id === segmentId
        );

        if (!segment) {
          toast.error(i18n.t("transcript.segmentNotFound"));
          return;
        }

        // Clean and format the text for a better bookmark title
        const cleanText = segment.text
          .trim()
          .replace(/\s+/g, " ") // Replace multiple spaces with single space
          .replace(/\n+/g, " "); // Replace line breaks with spaces

        // Create a smart title that doesn't cut words in the middle
        let title = cleanText;
        const maxLength = 40; // Increased from 30 for better readability

        if (cleanText.length > maxLength) {
          // Find the last space before the max length to avoid cutting words
          const truncateAt = cleanText.lastIndexOf(" ", maxLength);
          if (truncateAt > 20) {
            // Only use word boundary if it's not too short
            title = cleanText.substring(0, truncateAt) + "...";
          } else {
            // Fallback to character limit if no good word boundary found
            title = cleanText.substring(0, maxLength - 3) + "...";
          }
        }

        // Create a bookmark from this transcript segment
        addBookmark({
          name: title,
          start: segment.startTime,
          end: segment.endTime,
          mediaName: currentFile?.name,
          mediaType: currentFile?.type,
          youtubeId: currentYouTube?.id,
          playbackRate,
          annotation: segment.text, // Keep the full text as annotation
        });
      },

      // New loading action
      setIsLoadingMedia: (loading) => set({ isLoadingMedia: loading }),

      // Helper functions for media-scoped bookmarks
      getCurrentMediaId: () => {
        const { currentFile, currentYouTube } = get();
        if (currentFile) {
          return (
            currentFile.storageId ||
            currentFile.id ||
            `file-${currentFile.name}-${currentFile.size}`
          );
        }
        if (currentYouTube) {
          return `youtube-${currentYouTube.id}`;
        }
        return null;
      },
      getCurrentMediaBookmarks: () => {
        const { mediaBookmarks, getCurrentMediaId } = get();
        const mediaId = getCurrentMediaId();
        return mediaId ? mediaBookmarks[mediaId] || [] : [];
      },

      // Helper functions for media-scoped transcripts
      getCurrentMediaTranscripts: () => {
        const { mediaTranscripts, getCurrentMediaId } = get();
        const mediaId = getCurrentMediaId();
        return mediaId ? mediaTranscripts[mediaId] || [] : [];
      },
    }),
    {
      name: "abloop-player-storage",
      version: 3,
      migrate: (persistedState: any, version) => {
        if (!persistedState) return persistedState;

        if (version < 2 && persistedState.mediaFolders) {
          persistedState.mediaFolders = Object.fromEntries(
            Object.entries(persistedState.mediaFolders).map(
              ([id, folder]: [string, any]) => [
                id,
                {
                  ...folder,
                  parentId:
                    folder && "parentId" in folder ? folder.parentId ?? null : null,
                },
              ]
            )
          );
        }

        if (version < 2) {
          if (persistedState.historyFolderFilter === "all") {
            persistedState.historyFolderFilter = "unfiled";
          }
        }

        return persistedState;
      },
      onRehydrateStorage: () => (state, error) => {
        if (error || !state?.mediaTranscripts) {
          return;
        }

        const legacyTranscripts = state.mediaTranscripts;

        Object.entries(legacyTranscripts).forEach(([mediaId, segments]) => {
          void setStoredTranscript(mediaId, segments);
        });
      },
      partialize: (state) => ({
        volume: state.volume,
        muted: state.muted,
        playbackRate: state.playbackRate,
        theme: state.theme,
        waveformZoom: state.waveformZoom,
        showWaveform: state.showWaveform,
        videoSize: state.videoSize,
        mediaBookmarks: state.mediaBookmarks,
        showTranscript: state.showTranscript,
        transcriptLanguage: state.transcriptLanguage,
        recentYouTubeVideos: state.recentYouTubeVideos,
        mediaHistory: state.mediaHistory,
        historyLimit: state.historyLimit,
        mediaFolders: state.mediaFolders,
        historySortBy: state.historySortBy,
        historySortOrder: state.historySortOrder,
        historyFolderFilter: state.historyFolderFilter,
        seekStepSeconds: state.seekStepSeconds,
        seekSmallStepSeconds: state.seekSmallStepSeconds,
      }),
    }
  )
);
